/**
 * audio/sfx.js
 * Hệ thống Procedural Sound FX (0kB Synthesizer Web Audio API)
 * Dùng chung cho Drama Director, Storyboard Director và UI tactile feedback.
 */

export class SfxPlayer {
  /**
   * @param {object} [options]
   * @param {() => boolean|boolean} [options.isSoundEnabled=true] - Callback hoặc boolean kiểm tra âm thanh có bật không
   * @param {(intensity: number) => void} [options.onScreenShake] - Callback kích hoạt rung màn hình khi có va đập mạnh
   * @param {AudioContext} [options.audioCtx] - AudioContext tuỳ chọn dùng chung
   */
  constructor(options = {}) {
    this._getSoundEnabled =
      typeof options.isSoundEnabled === 'function' ? options.isSoundEnabled : () => options.isSoundEnabled !== false;

    this.onScreenShake = options.onScreenShake || null;
    this.audioCtx = options.audioCtx || null;

    if (!this.audioCtx) {
      this.initAudioContext();
    }
  }

  get isSoundEnabled() {
    return this._getSoundEnabled();
  }

  initAudioContext() {
    try {
      if (typeof window !== 'undefined') {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx && !this.audioCtx) {
          this.audioCtx = new AudioCtx();
        }
      }
    } catch (err) {
      console.warn('AudioContext không thể khởi tạo:', err);
    }
  }

  resumeAudio() {
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {});
    }
  }

  /**
   * Tiếng lướt gió / vung tay (Whoosh)
   * @param {'drama'|'storyboard'} [variant='drama'] - Biến thể âm thanh cho Street Drama hoặc Storyboard Director
   */
  playWhoosh(variant = 'drama') {
    if (!this.isSoundEnabled || !this.audioCtx) return;
    this.resumeAudio();
    try {
      const ctx = this.audioCtx;
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();

      filter.type = 'bandpass';
      if (variant === 'storyboard') {
        filter.frequency.setValueAtTime(400, now);
        filter.frequency.exponentialRampToValueAtTime(1400, now + 0.2);
        filter.frequency.exponentialRampToValueAtTime(300, now + 0.45);
        filter.Q.value = 2.0;

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(100, now);
        osc.frequency.exponentialRampToValueAtTime(250, now + 0.2);
        osc.frequency.exponentialRampToValueAtTime(60, now + 0.45);

        gain.gain.setValueAtTime(0.01, now);
        gain.gain.linearRampToValueAtTime(0.3, now + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.55);
      } else {
        filter.frequency.setValueAtTime(500, now);
        filter.frequency.exponentialRampToValueAtTime(1200, now + 0.25);
        filter.frequency.exponentialRampToValueAtTime(250, now + 0.55);
        filter.Q.value = 2.5;

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(90, now);
        osc.frequency.exponentialRampToValueAtTime(220, now + 0.25);
        osc.frequency.exponentialRampToValueAtTime(60, now + 0.55);

        gain.gain.setValueAtTime(0.01, now);
        gain.gain.linearRampToValueAtTime(0.35, now + 0.25);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.65);
      }
    } catch {}
  }

  /**
   * Tiếng va chạm đấm cực mạnh (Super Punch Impact)
   */
  playPunchImpact() {
    if (!this.isSoundEnabled || !this.audioCtx) return;
    this.resumeAudio();
    try {
      const ctx = this.audioCtx;
      const now = ctx.currentTime;

      // 1. Âm bass trầm đập mạnh (Super Punch Thud)
      const osc = ctx.createOscillator();
      const oscGain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(180, now);
      osc.frequency.exponentialRampToValueAtTime(28, now + 0.35);

      oscGain.gain.setValueAtTime(0.85, now);
      oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);

      osc.connect(oscGain);
      oscGain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.4);

      // 2. Âm va chạm đanh thép (Punch Crack Noise)
      const noiseBuffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.18), ctx.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      for (let i = 0; i < noiseBuffer.length; i++) {
        output[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.03));
      }
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;

      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = 'lowpass';
      noiseFilter.frequency.setValueAtTime(1200, now);
      noiseFilter.frequency.exponentialRampToValueAtTime(150, now + 0.18);

      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.7, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

      noise.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(ctx.destination);
      noise.start(now);

      // Rung màn hình
      if (typeof this.onScreenShake === 'function') {
        this.onScreenShake(0.5);
      }
    } catch {}
  }

  /**
   * Tiếng toàn thân đập xuống đất (Body Fall / Ground Slam)
   */
  playBodyFall() {
    if (!this.isSoundEnabled || !this.audioCtx) return;
    this.resumeAudio();
    try {
      const ctx = this.audioCtx;
      const now = ctx.currentTime;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(110, now);
      osc.frequency.exponentialRampToValueAtTime(20, now + 0.5);

      gain.gain.setValueAtTime(0.8, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.55);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.58);

      // Tiếng va chạm mặt đường bê tông
      const noiseBuffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.25), ctx.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      for (let i = 0; i < noiseBuffer.length; i++) {
        output[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.05));
      }
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;

      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(450, now);
      filter.frequency.exponentialRampToValueAtTime(70, now + 0.25);

      const nGain = ctx.createGain();
      nGain.gain.setValueAtTime(0.65, now);
      nGain.gain.exponentialRampToValueAtTime(0.001, now + 0.26);

      noise.connect(filter);
      filter.connect(nGain);
      nGain.connect(ctx.destination);
      noise.start(now);

      if (typeof this.onScreenShake === 'function') {
        this.onScreenShake(0.35);
      }
    } catch {}
  }

  /**
   * Tiếng còi xe cộ đường phố (City Traffic Horn)
   */
  playCityHonk() {
    if (!this.isSoundEnabled || !this.audioCtx) return;
    this.resumeAudio();
    try {
      const ctx = this.audioCtx;
      const now = ctx.currentTime;

      [440, 480].forEach((f) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(f, now);

        gain.gain.setValueAtTime(0.04, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.26);
      });
    } catch {}
  }

  /**
   * Tiếng ting cảm xúc tươi sáng (Emotion Ting)
   */
  playEmotionTing() {
    if (!this.isSoundEnabled || !this.audioCtx) return;
    this.resumeAudio();
    try {
      const ctx = this.audioCtx;
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(1200, now);
      osc.frequency.exponentialRampToValueAtTime(1800, now + 0.15);

      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.4);
    } catch {}
  }

  /**
   * Tiếng click phản hồi cơ học xúc giác khi nhấn nút
   * @param {number} [freq=520]
   */
  playTactileFeedback(freq = 520) {
    try {
      this.initAudioContext();
      if (!this.audioCtx) return;
      this.resumeAudio();
      const ctx = this.audioCtx;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const now = ctx.currentTime;

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now);
      osc.frequency.exponentialRampToValueAtTime(220, now + 0.04);
      gain.gain.setValueAtTime(0.04, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.04);
    } catch {}
  }

  dispose() {
    if (this.audioCtx && typeof this.audioCtx.close === 'function') {
      try {
        this.audioCtx.close();
      } catch {}
      this.audioCtx = null;
    }
  }
}

/** Instance đơn lẻ cho các tương tác UI nhanh */
export const defaultSfx = new SfxPlayer();
